import React, { createContext, useContext } from "react";
import {
  DATA_SOURCE_PATHS,
  type PathEntry,
} from "../../../util/src/layout/data-source-schema.js";
import type { DataSourceId } from "../../../util/src/layout/types.js";

export type BehaviorPathSchemaMap = Record<string, PathEntry[] | null>;

const BehaviorPathSchemaContext = createContext<BehaviorPathSchemaMap | null>(
  null,
);

/**
 * Resolves the path schema for an object/list source.
 *
 * Custom discipline sources (`athlete`, `athletes`) are taken from the overlay
 * when present. Layout DisplayEditor sources keep using {@link DATA_SOURCE_PATHS}.
 *
 * @param source - Variable `objectSource` (layout DataSourceId or custom key).
 * @param custom - Optional overlay supplied by the custom-discipline editor.
 * @returns Path entries for the `get` dropdown, or null when the source is scalar.
 */
export function resolveBehaviorPathSchema(
  source: string | undefined,
  custom: BehaviorPathSchemaMap | null,
): PathEntry[] | null {
  if (!source) {
    return null;
  }
  if (custom && Object.prototype.hasOwnProperty.call(custom, source)) {
    return custom[source] ?? null;
  }
  return DATA_SOURCE_PATHS[source as DataSourceId] ?? null;
}

/**
 * Provides a path-schema overlay to operator nodes inside {@link BehaviorCanvas}.
 * DisplayEditor does not mount this provider, so operators keep layout schemas.
 */
export function BehaviorPathSchemaProvider({
  schema,
  children,
}: {
  schema: BehaviorPathSchemaMap | null;
  children: React.ReactNode;
}) {
  return (
    <BehaviorPathSchemaContext.Provider value={schema}>
      {children}
    </BehaviorPathSchemaContext.Provider>
  );
}

/**
 * Path schema for the given object source, overlaying custom-discipline fields
 * when the admin editor provided a map.
 *
 * @param source - Variable `objectSource`.
 * @returns Path entries, or null when the source has no fields.
 */
export function useBehaviorPathSchema(
  source: string | undefined,
): PathEntry[] | null {
  const custom = useContext(BehaviorPathSchemaContext);
  return resolveBehaviorPathSchema(source, custom);
}
